<template>
  <div>
    <div ref="containerTop">
      <button-group>
        <button
          @click="resetFilters()"
          class="button-search flex-0 h-full rounded text-interaction-light button-reset"
          v-if="filtersActive"
        >
          X
        </button>
        <filter-button
          :active="!!$query.topicFilter"
          @click="showFilter = 'topic'"
        >
          {{ $t("views.government.vote.themes_filter") }}
        </filter-button>
        <filter-button
          :active="!!($query.from || $query.to)"
          @click="showFilter = 'date'"
        >
          {{ $t("views.government.vote.range_filter") }}
        </filter-button>
        <filter-button
          @click="showFilter = 'decision'"
          :active="!!checkedDecision.length"
        >
          {{ $t("views.government.vote.votes") }}
        </filter-button>
        <button
          @click="showFilter = 'search'"
          class="button-search flex-0 h-full rounded"
          :class="{
            'bg-interaction-light text-color-regular': $query.searchTerm,
            'text-interaction-light': !$query.searchTerm
          }"
        >
          <icon-search />
        </button>
      </button-group>
    </div>
    <tag-container
      class="my-4"
      v-if="
        $query.searchTerm ||
          $query.from ||
          $query.to ||
          checkedDecision.length ||
          checkedThemes.length
      "
    >
      <tag-row>
        <closeable-search-tag
          :search="$query.searchTerm"
          @close="$query.searchTerm = ''"
          @open="showFilter = 'search'"
        />
        <closeable-date-tag
          :from="$query.from"
          :to="$query.to"
          @close="removeDateFilter"
          @open="showFilter = 'date'"
        />
      </tag-row>
      <tag-row>
        <closeable-theme-tag
          v-for="theme in themes.filter(t => checkedThemes.includes(t.id))"
          :key="theme.id"
          :theme="theme"
          @close="removeThemeFromFilter(theme.id)"
          @open="showFilter = 'topic'"
        />
      </tag-row>
      <tag-row>
        <closeable-decision-tag
          v-for="decision in checkedDecision"
          :key="decision"
          :label="allVotes[decision]"
          @close="removeDecisionFromFilter(decision)"
          @open="showFilter = 'decision'"
        />
      </tag-row>
    </tag-container>
    <div class="relative flex flex-col" style="flex-grow: 1">
      <template v-if="isLoading">
        <div class="relative" style="height: 50vh">
          <icon-loading-spinner
            :fade="true"
            color="gray"
            class="absolute-center"
          />
        </div>
      </template>
      <template v-else>
        <div class="flex flex-col" style="padding-bottom: 1.624rem;">
          <vote-overview
            :voteItems="voteItemsWithDividers"
            :userVotes="userVotes"
            :partyColor="partyColor"
            :candidateId="candidateId"
            :showPin="showPins"
            :pinnedVotes="pinnedVotes"
            @pinned="id => $emit('pinned', id)"
            gridClass="grid grid-cols-1 lg:grid-cols-2 gap-1 items-stretch"
          />
          <div class="vote-overlays">
            <decision-filter-modal
              v-if="showFilter == 'decision'"
              :checked="checkedDecision"
              @validate="onDecisionValidate"
              @close="showFilter = ''"
            />
            <search-filter-modal
              v-if="showFilter == 'search'"
              @validate="onSearchValidate"
              @close="showFilter = ''"
              :value="$query.searchTerm"
            />
            <date-range-filter-modal
              v-if="showFilter == 'date'"
              @validate="onDateValidate"
              @close="showFilter = ''"
              :from="$query.from"
              :to="$query.to"
            />
            <theme-filter-modal
              v-if="showFilter == 'topic'"
              @validate="onTopicValidate"
              @close="showFilter = ''"
              :checked="checkedThemes"
              :themes="themes"
            />
          </div>
        </div>
        <page-buttons
          class="justify-center w-full"
          :current="parseInt($query.page || 1)"
          :pageCount="lastPage"
          @change="changePage"
        />
      </template>
    </div>
  </div>
</template>

<script>
// Utilities
import { ViewStateMixin } from "../../utils/ViewStateMixin";
import client from "api-client";
import { VoteMixin } from "@/utils/VoteMixin";

// Components
import ButtonGroup from "@/components/buttons/ButtonGroup";
import VoteOverview from "@/components/vote/VoteOverview.vue";
import FilterButton from "@/components/buttons/filters/FilterButton.vue";
import PageButtons from "@/components/buttons/PageButtons.vue";

// Icons
import IconLoadingSpinner from "@/components/icons/IconLoadingSpinner";
import IconSearch from "@/components/icons/IconSearch.vue";
import { mapGetters } from "vuex";
import DecisionFilterModal from "../modals/DecisionFilterModal.vue";
import ThemeFilterModal from "../modals/ThemeFilterModal.vue";
import DateRangeFilterModal from "../modals/DateRangeFilterModal.vue";
import SearchFilterModal from "../modals/SearchFilterModal.vue";
import CloseableDateTag from "../misc/CloseableDateTag.vue";
import TagContainer from "../misc/TagContainer.vue";
import TagRow from "../misc/TagRow.vue";
import CloseableDecisionTag from "../misc/CloseableDecisionTag.vue";
import CloseableThemeTag from "../misc/CloseableThemeTag.vue";
import CloseableSearchTag from "../misc/CloseableSearchTag.vue";

export default {
  name: "VoteCouncillorTab",
  components: {
    ButtonGroup,
    VoteOverview,
    // Icons
    IconLoadingSpinner,
    IconSearch,
    FilterButton,
    PageButtons,
    DecisionFilterModal,
    ThemeFilterModal,
    DateRangeFilterModal,
    SearchFilterModal,
    CloseableDateTag,
    CloseableDecisionTag,
    CloseableThemeTag,
    CloseableSearchTag,
    TagContainer,
    TagRow
  },
  props: {
    lightTheme: {
      type: Boolean
    },
    partyColor: {
      type: String
    },
    candidateId: {
      type: Number
    },
    pinnedVotes: {
      type: Object,
      default: () => ({})
    }
  },
  mixins: [ViewStateMixin, VoteMixin],
  data: () => ({
    isLoading: false,
    lastPage: 1,
    votes: [],
    themes: [],
    userVotes: {},
    showFilter: ""
  }),
  computed: {
    ...mapGetters("user", ["contentLanguage", "user", "isLoggedIn"]),
    ...mapGetters("notes", ["showPins", "selectedColor"]),
    checkedDecision: {
      get() {
        return this.$query.decisions ? this.$query.decisions.split(",") : [];
      },
      set(value) {
        this.replaceQuery(
          {
            ...this.$query,
            decisions: value.join(",")
          },
          ""
        );
      }
    },
    checkedThemes: {
      get() {
        return this.$query.topicFilter
          ? this.$query.topicFilter.split(",").map(n => parseInt(n))
          : [];
      },
      set(value) {
        this.$query.topicFilter = value.join(",");
      }
    },
    filtersActive() {
      return ["from", "to", "topicFilter", "searchTerm", "peopleVotes"].some(
        key => !!this.$query[key]
      );
    },
    voteItemsWithDividers() {
      let lastSession = 0;
      const voteItemsWithDivider = [];

      for (const vote of this.votes) {
        if (vote.session.id != lastSession) {
          voteItemsWithDivider.push({
            type: "divider",
            label: vote.session.name
          });
          lastSession = vote.session.id;
        }
        voteItemsWithDivider.push(vote);
      }

      return voteItemsWithDivider;
    }
  },
  async mounted() {
    this.fetchCouncillorVotes();
    const [themes, userVotes] = await Promise.all([
      client.fetchAffairTopics(this.contentLanguage),
      this.isLoggedIn
        ? client.fetchUserVotes(this.user.id)
        : Promise.resolve([])
    ]);
    this.themes = themes;
    this.userVotes = Object.assign({}, ...(userVotes || []));

    if (this.$route.hash) {
      this.$nextTick(() => {
        const selectedVote = document.querySelector(this.$route.hash);
        if (selectedVote) {
          return selectedVote.scrollIntoView({ behavior: "auto" });
        }
      });
    }
  },
  watch: {
    $query(oldQuery, newQuery) {
      const watched = [
        "decisions",
        "page",
        "topicFilter",
        "from",
        "to",
        "searchTerm"
      ];
      for (const key of watched) {
        if (oldQuery[key] != newQuery[key]) {
          if (key !== "page") {
            this.replaceQuery(
              {
                ...this.$query,
                page: 1
              },
              ""
            );
          }
          this.isLoading = true;
          this.fetchCouncillorVotes();
          break;
        }
      }
    }
  },
  methods: {
    async fetchCouncillorVotes() {
      this.isLoading = true;
      const paginatedData = await client.fetchCouncillorVotes(
        this.candidateId,
        {
          ...this.$query
        },
        this.contentLanguage
      );
      this.votes = paginatedData.data;
      this.lastPage = paginatedData.meta.last_page;
      if (this.lastPage < parseInt(this.$query.page)) {
        this.$query.page = 1;
      }
      this.isLoading = false;
    },
    changePage(index) {
      this.$nextTick(() => {
        const listElement = this.$refs.containerTop;
        listElement.scrollIntoView({ behavior: "auto" });
      });
      this.replaceQuery(
        {
          ...this.$query,
          page: index
        },
        ""
      );
    },
    resetFilters() {
      const newQuery = {
        ...this.$query,
        page: 1
      };

      for (const key of [
        "topicFilter",
        "from",
        "to",
        "decisions",
        "searchTerm"
      ]) {
        delete newQuery[key];
      }
      this.replaceQuery(newQuery);
    },
    onDecisionValidate(newValues) {
      this.checkedDecision = newValues;
      this.showFilter = "";
    },
    onSearchValidate(newValue) {
      this.$query.searchTerm = newValue;
      this.showFilter = "";
    },
    onDateValidate(newValue) {
      this.replaceQuery({
        ...this.$query,
        ...newValue
      });
      this.showFilter = "";
    },
    onTopicValidate(newValue) {
      this.checkedThemes = newValue;
      this.showFilter = "";
    },
    removeDateFilter() {
      const newQuery = { ...this.$query };
      delete newQuery["from"];
      delete newQuery["to"];
      this.replaceQuery(newQuery);
    },
    removeThemeFromFilter(id) {
      this.checkedThemes = this.checkedThemes.filter(tid => tid != id);
    },
    removeDecisionFromFilter(value) {
      this.checkedDecision = this.checkedDecision.filter(v => v != value);
    }
  }
};
</script>
